import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Search, 
  Trash, 
  Loader2,
  AlertCircle,
  Mail,
  Phone,
  FileText,
  User,
  Building2
} from "lucide-react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/use-toast";

const VacanciesFeedbackAdmin = () => {
  const [search, setSearch] = useState("");
  const [deletingApplicationId, setDeletingApplicationId] = useState<string | null>(null);
  const { session } = useAuth();
  
  const queryClient = useQueryClient();
  
  const { 
    data: applications = [], 
    isLoading, 
    isError 
  } = useQuery({
    queryKey: ['vacancy-applications'],
    queryFn: api.vacancyApplications.getAll,
    enabled: !!session // Запрос будет выполнен только если есть активная сессия
  });
  
  const filteredApplications = applications.filter((application: any) =>
    application.name.toLowerCase().includes(search.toLowerCase()) ||
    application.email.toLowerCase().includes(search.toLowerCase()) ||
    application.vacancy_title.toLowerCase().includes(search.toLowerCase()) ||
    application.cover_letter?.toLowerCase().includes(search.toLowerCase())
  );

  const handleDeleteApplication = async () => {
    if (deletingApplicationId) {
      await api.vacancyApplications.delete(deletingApplicationId);
      setDeletingApplicationId(null);
      queryClient.invalidateQueries({ queryKey: ['vacancy-applications'] });
    }
  };

  const handleDownloadResume = async (resumeUrl: string) => {
    try {
      const response = await fetch(resumeUrl);
      
      if (!response.ok) {
        throw new Error('Failed to download resume');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resumeUrl.split('/').pop() || 'resume.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast({
        title: "Ошибка загрузки",
        description: "Не удалось загрузить резюме. Пожалуйста, попробуйте позже.",
        variant: "destructive",
      });
    }
  };
  

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Отклики на вакансии</h1>
      </div>
      
      <div className="relative w-full max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Поиск откликов..."
          className="pl-8"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="flex justify-center items-center py-8 text-destructive">
          <AlertCircle className="h-8 w-8 mr-2" />
          <p>Ошибка загрузки откликов. Пожалуйста, попробуйте позже.</p>
        </div>
      ) : filteredApplications.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Откликов не найдено
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Кандидат</TableHead>
                <TableHead>Вакансия</TableHead>
                <TableHead>Контактная информация</TableHead>
                <TableHead>Сопроводительное письмо</TableHead>
                <TableHead>Резюме</TableHead>
                <TableHead>Дата</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead className="w-[100px]">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredApplications.map((application: any) => (
                <TableRow key={application.id}>
                  <TableCell>
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2 text-muted-foreground" />
                      {application.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                      {application.vacancy_title}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                        <a href={`mailto:${application.email}`} className="text-primary hover:underline">
                          {application.email}
                        </a>
                      </div>
                      {application.phone && (
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                          <a href={`tel:${application.phone}`} className="text-primary hover:underline">
                            {application.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-pre-wrap break-words max-w-[300px]">
                    {application.cover_letter || "Не указано"}
                  </TableCell>
                  <TableCell>
                    {application.resume_url ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadResume(application.resume_url)}
                        className="flex items-center text-primary hover:underline"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Скачать
                      </Button>
                    ) : (
                      <span className="text-muted-foreground">Не загружено</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {format(new Date(application.created_at), 'dd.MM.yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      application.status === 'new' ? 'bg-blue-100 text-blue-800' :
                      application.status === 'reviewed' ? 'bg-yellow-100 text-yellow-800' :
                      application.status === 'accepted' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {application.status === 'new' ? 'Новое' :
                       application.status === 'reviewed' ? 'Просмотрено' :
                       application.status === 'accepted' ? 'Принято' :
                       'Отклонено'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeletingApplicationId(application.id)}
                    >
                      <Trash className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      
      <AlertDialog open={!!deletingApplicationId} onOpenChange={() => setDeletingApplicationId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить отклик?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Отклик будет удален навсегда.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteApplication} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VacanciesFeedbackAdmin; 